import { useState } from "react";
import { api } from "../api";
import { StatusBadge } from "./Ui";

export type KeyField = { key: string; label: string; placeholder: string; secret: boolean; required: boolean; client: "client" | "unreadable" | null };
export type ClientProvider = {
  id: string; name: string; category: string; description: string; testable: boolean;
  source: "client" | "agency" | "none" | "unreadable"; partial: boolean; fields: KeyField[];
};

const SOURCE: Record<ClientProvider["source"], [string, "success" | "neutral" | "warning" | "danger"]> = {
  client: ["Using this client's key", "success"],
  agency: ["Using agency key", "neutral"],
  none: ["Not set anywhere", "warning"],
  unreadable: ["Stored key can't be read. Replace it.", "danger"],
};

/**
 * A client's own copies of agency keys: one panel, one row per service (name
 * and what it is for on the left, where its key comes from and the action on
 * the right). Values go in and never come back.
 */
export default function ClientKeys({ clientId, providers, vaultReady, onChanged }: {
  clientId: number; providers: ClientProvider[]; vaultReady: boolean; onChanged: () => void;
}) {
  return (
    <section className="panel client-key-list" aria-label="Client API keys">
      {providers.map((p) => <KeyRow key={p.id} clientId={clientId} provider={p} vaultReady={vaultReady} onChanged={onChanged} />)}
      {!vaultReady && <p className="client-keys-vault">The credential vault is not configured on this deployment, so client keys can't be saved.</p>}
    </section>
  );
}

function KeyRow({ clientId, provider, vaultReady, onChanged }: { clientId: number; provider: ClientProvider; vaultReady: boolean; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<"" | "save" | "remove" | "test">("");
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const base = `/api/client-settings/${clientId}/credentials/${provider.id}`;
  const [label, tone] = SOURCE[provider.source];
  const hasOwn = provider.fields.some((f) => f.client);

  async function run(kind: "save" | "remove" | "test", fn: () => Promise<string>) {
    setBusy(kind); setNotice(null);
    try { setNotice({ ok: true, text: await fn() }); onChanged(); }
    catch (e) { setNotice({ ok: false, text: e instanceof Error ? e.message : "Something went wrong" }); }
    finally { setBusy(""); }
  }

  return (
    <div className="client-key-row">
      <div className="client-key-line">
        <div className="client-key-name">
          <h3>{provider.name}</h3>
          <p>{provider.description}</p>
        </div>
        <div className="client-key-side">
          <StatusBadge tone={tone}>{label}</StatusBadge>
          {!open && (
            <div className="client-key-actions">
              <button className="button secondary" disabled={!vaultReady} onClick={() => setOpen(true)}>{hasOwn ? "Replace" : "Add client key"}</button>
              {provider.testable && provider.source !== "none" && (
                <button className="button secondary" disabled={busy !== ""} onClick={() => void run("test", async () => {
                  const r = await api<{ message: string; source: string }>(`${base}/test`, { method: "POST", body: "{}" });
                  return `${r.source === "client" ? "Client key" : "Agency key"}: ${r.message}`;
                })}>{busy === "test" ? "Testing…" : "Test"}</button>
              )}
              {hasOwn && (
                <button className="text-button" disabled={busy !== ""} onClick={() => {
                  // "agency"/"none" mean we already know (from the agency's own
                  // required-key check) whether an agency key is fully set;
                  // "client"/"unreadable" don't reveal that, so stay neutral.
                  const removeCopy =
                    provider.source === "agency"
                      ? `Remove this client's ${provider.name} key? It will use the agency key.`
                      : provider.source === "none"
                        ? `Remove this client's ${provider.name} key? No agency key is set, so this integration will stop working for this client.`
                        : `Remove this client's ${provider.name} key? Work for this client will fall back to the agency key if one is set.`;
                  if (window.confirm(removeCopy))
                    void run("remove", async () => (await api<{ message: string }>(base, { method: "DELETE" })).message);
                }}>Remove</button>
              )}
            </div>
          )}
        </div>
      </div>
      {provider.partial && <p className="integration-notice danger">Only part of this client's key set is saved, so the agency key is used.</p>}
      {open && (
        <form className="credential-fields" onSubmit={(e) => {
          e.preventDefault();
          void run("save", async () => {
            const r = await api<{ message: string }>(base, { method: "PUT", body: JSON.stringify({ values }) });
            setValues({}); setOpen(false);
            return r.message;
          });
        }}>
          {provider.fields.map((f) => (
            <label key={f.key} className="credential-field">
              <span>{f.label}{f.client === "client" ? " · saved" : ""}</span>
              <input type={f.secret ? "password" : "text"} autoComplete="new-password" value={values[f.key] || ""}
                placeholder={f.client === "client" ? "Enter a replacement" : f.placeholder}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
            </label>
          ))}
          <div className="client-key-actions">
            <button className="button" disabled={!vaultReady || busy !== "" || !Object.values(values).some((v) => v.trim())}>
              {busy === "save" ? "Saving…" : "Save for this client"}
            </button>
            <button type="button" className="button secondary" onClick={() => { setOpen(false); setValues({}); }}>Cancel</button>
          </div>
        </form>
      )}
      {notice && <p role={notice.ok ? "status" : "alert"} className={`integration-notice ${notice.ok ? "success" : "danger"}`}>{notice.text}</p>}
    </div>
  );
}
